import { apiPost } from "~/shared/api/client";
import type { AttachmentCreate } from "~/entities/chat";
import type { PendingUpload } from "~/shared/store/types";
import type {
    CompleteMultipartUploadResponseDto,
    StartMultipartUploadResponseDto,
} from "~/shared/types/chats";
import { computeSha256 } from "~/shared/lib/uploads/compute-sha256";
import { mediaKindFromMime } from "~/shared/lib/uploads/media-kind";
import { putWithProgress, uploadFileMultipartWithProgress } from "~/shared/lib/xhr-upload";

import { makeUploadLocalId, needsPreview } from "./upload-attachment-helpers";

interface PresignResponse {
    urls: { uploadUrl: string; s3Key: string; s3Url: string }[];
}

type UpdatePendingFn = (chatId: string, localId: string, patch: Partial<PendingUpload>) => void;

export interface UploadStrategyCtx {
    chatId: string;
    updatePending: UpdatePendingFn;
    onPercent: (percent: number) => void;
    // Хэш файла, посчитанный один раз в `uploadPendingFile`; undefined в
    // небезопасном контексте (нет crypto.subtle) — тогда attachment без хэша.
    sha256: string | undefined;
}

/**
 * Throttle-репортер прогресса: пушит в store только когда целое значение %
 * изменилось (избегаем лишних set'ов на каждый XHR-tick).
 */
export function createPercentReporter(
    chatId: string,
    localId: string,
    updatePending: UpdatePendingFn,
): (percent: number) => void {
    let last = 0;
    return (percent: number) => {
        if (percent === last) return;
        last = percent;
        updatePending(chatId, localId, { progress: percent });
    };
}

const originFilename = (file: File): string => file.name.replace(/\.[^.]+$/, "");

/**
 * Optimistic pending-seed для одного файла (до presign). previewUrl — blob для
 * image/video, иначе undefined (см. `needsPreview`). `URL.createObjectURL` —
 * намеренный side-effect: blob освобождается стором при remove/clear.
 */
export function buildPendingSeed(file: File): PendingUpload {
    const mimeType = file.type || "application/octet-stream";
    return {
        localId: makeUploadLocalId(file),
        fileName: file.name,
        kind: mediaKindFromMime(mimeType, "image"),
        mimeType,
        sizeBytes: file.size,
        status: "presigning",
        progress: 0,
        previewUrl: needsPreview(file.type) ? URL.createObjectURL(file) : undefined,
    };
}

/**
 * Загрузка одного pending-файла: выбор стратегии по размеру + обработка ошибки
 * (помечает seed `error` в сторе). Возвращает `AttachmentCreate` при успехе,
 * иначе `null` — так тело цикла в хуке остаётся плоским.
 */
export async function uploadPendingFile(
    file: File,
    seed: PendingUpload,
    deps: { chatId: string; updatePending: UpdatePendingFn },
    multipartThreshold: number,
): Promise<AttachmentCreate | null> {
    const ctx: UploadStrategyCtx = {
        chatId: deps.chatId,
        updatePending: deps.updatePending,
        onPercent: createPercentReporter(deps.chatId, seed.localId, deps.updatePending),
        sha256: await computeSha256(file),
    };
    try {
        return file.size > multipartThreshold
            ? await uploadViaMultipart(file, seed, ctx)
            : await uploadViaSinglePut(file, seed, ctx);
    } catch (err) {
        deps.updatePending(deps.chatId, seed.localId, {
            status: "error",
            error: err instanceof Error ? err.message : String(err),
        });
        return null;
    }
}

/** Multipart-ветка для больших файлов (size > multipartThreshold). */
export async function uploadViaMultipart(
    file: File,
    seed: PendingUpload,
    ctx: UploadStrategyCtx,
): Promise<AttachmentCreate> {
    const { chatId, updatePending, onPercent } = ctx;

    const startResp = await apiPost<StartMultipartUploadResponseDto>("/v2/files/multipart-upload/start", {
        destination: "chat_attachments",
        chat_id: chatId,
        kind: seed.kind,
        mime_type: seed.mimeType,
        size_bytes: seed.sizeBytes,
        origin_filename: originFilename(file),
    });

    updatePending(chatId, seed.localId, {
        status: "uploading",
        s3Key: startResp.s3Key,
        progress: 0,
    });

    const completedParts = await uploadFileMultipartWithProgress(file, startResp, onPercent);

    await apiPost<CompleteMultipartUploadResponseDto>("/v2/files/multipart-upload/complete", {
        upload_id: startResp.uploadId,
        s3_key: startResp.s3Key,
        parts: completedParts.map((p) => ({ part_number: p.partNumber, etag: p.etag })),
    });

    updatePending(chatId, seed.localId, { status: "done", progress: 100, sha256: ctx.sha256 });
    return {
        s3Key: startResp.s3Key,
        kind: seed.kind,
        mimeType: seed.mimeType,
        sizeBytes: seed.sizeBytes,
        status: "uploaded",
        sha256: ctx.sha256,
    };
}

/** Single-PUT ветка (default) для файлов в пределах порога. */
export async function uploadViaSinglePut(
    file: File,
    seed: PendingUpload,
    ctx: UploadStrategyCtx,
): Promise<AttachmentCreate> {
    const { chatId, updatePending, onPercent } = ctx;

    const presignRes = await apiPost<PresignResponse>("/v2/files/signed-urls", {
        files: [
            {
                destination: "chat_attachments",
                chat_id: chatId,
                kind: seed.kind,
                mime_type: seed.mimeType,
                size_bytes: seed.sizeBytes,
                origin_filename: originFilename(file),
            },
        ],
    });
    const signed = presignRes.urls[0];
    if (!signed?.uploadUrl) throw new Error("Empty presigned response");

    updatePending(chatId, seed.localId, {
        status: "uploading",
        s3Key: signed.s3Key,
        progress: 0,
    });

    await putWithProgress(signed.uploadUrl, file, {
        mimeType: seed.mimeType,
        onProgress: (loaded, total) => {
            if (total <= 0) return;
            onPercent(Math.floor((loaded / total) * 100));
        },
    });
    onPercent(100);

    updatePending(chatId, seed.localId, { status: "done", progress: 100, sha256: ctx.sha256 });
    return {
        s3Key: signed.s3Key,
        kind: seed.kind,
        mimeType: seed.mimeType,
        sizeBytes: seed.sizeBytes,
        status: "uploaded",
        sha256: ctx.sha256,
    };
}
